// React
import { useState, useEffect } from "react"

// UI
import { Button } from "@/components/Button"
import { Tooltip } from "@/components/Tooltip"
import { Heading } from "@/components/Typography"

// DateTime
import { useLocalizedDateTime } from "@/lib/dates"

// Language
import { useLang } from "@/context/useLang"

// Virtual PWA modules
import * as PWAInfo from "virtual:pwa-info"

// Env variables
import {
	// Vercel
	VERCEL_ENV,
	VERCEL_DOMAIN,
	VERCEL_GIT_COMMIT_SHA,
	VERCEL_GIT_COMMIT_MESSAGE,
	VERCEL_GIT_COMMIT_AUTHOR_NAME,
	GRAPHQL_URL,
	API_URL,

	// I18n
	DEFAULT_LANG,
	DEFAULT_TIMEZONE,

	// External
	EXTERNAL_PLATFORM_INFO_CENTER,
	EXTERNAL_PLATFORM_ZONHUB_STATS_COMPANY,
	EXTERNAL_PLATFORM_ZONHUB_FINANCE,
	EXTERNAL_PLATFORM_ZONHUB_INVESTMENT,
	EXTERNAL_PLATFORM_ADMIN_PANEL,
	EXTERNAL_GOOGLE_REVIEW_URL,

	// Server
	SERVER_CSP_NONCE,
	MOCK_BACKEND,
	STORYBOOK_API_URL,
	VITEST_WORKER_ID,
	DEBUG_I18N,

	// Integrations
	SENTRY_DSN,
	SENTRY_DEBUG,
	SENTRY_ENABLED,
	GOOGLE_MAPS_API_KEY,
	GOOGLE_MAPS_ID,
	GTM_CONTAINER_ID,
	PLAUSIBLE_DOMAIN,
	FRESHDESK_WIDGET_ID,

	// Auth
	AUTH_LOGOUT_URL,
	AUTH_LOGIN_URL,
	AUTH_MFA_URL,
} from "@/lib/env"
import { CardBody, CardWrapper } from "@/components/Card"

/**
 * Info
 * @returns
 */
export function Info() {
	// Dates
	const DateTime = useLocalizedDateTime()

	// Translate
	const { lang, formatCurrency } = useLang()

	// Template
	return (
		<div className="space-y-6 p-4">
			{/* Page Title */}
			<CardWrapper>
				<CardBody>
					<Heading
						as="h1"
						styleAs="h4"
						className="text-center"
						data-testid="investments:fiscal.header"
					>
						Environment Variables & System Information
					</Heading>
				</CardBody>
			</CardWrapper>

			{/* Localization Section */}
			<CardWrapper>
				<CardBody>
					<Heading
						as="h2"
						styleAs="h5"
						className="mb-4 border-b border-gray-200 pb-2"
					>
						Localization
					</Heading>
					<div className="space-y-3">
						<Tooltip content="Apps default language">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									DEFAULT_LANG
								</span>
								<span className="text-gray-900">
									{DEFAULT_LANG}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Apps default timezone">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									DEFAULT_TIMEZONE
								</span>
								<span className="text-gray-900">
									{DEFAULT_TIMEZONE}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Users active language">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									Active language
								</span>
								<span className="text-gray-900">{lang}</span>
							</div>
						</Tooltip>

						<Tooltip content="Date string localised using users preferred language">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									Localized date (month)
								</span>
								<span className="text-gray-900">
									{DateTime.now().toLocaleString({
										month: "long",
									})}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Localized currency format">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									Localized currency
								</span>
								<span className="text-gray-900">
									{formatCurrency(1000000000.0)}
								</span>
							</div>
						</Tooltip>
					</div>
				</CardBody>
			</CardWrapper>

			{/* Deployment Section */}
			<CardWrapper>
				<CardBody>
					<Heading
						as="h2"
						styleAs="h5"
						className="mb-4 border-b border-gray-200 pb-2"
					>
						Deployment
					</Heading>
					<div className="space-y-3">
						<Tooltip content="The Environment that the app is deployed and running on. The value can be either production, preview, or development.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									VERCEL_ENV
								</span>
								<span className="text-gray-900">
									{VERCEL_ENV ?? "n/a"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="The MAIN domain name that is assigned as an alias to the deployment URL.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									VERCEL_DOMAIN
								</span>
								<span className="text-gray-900">
									{VERCEL_DOMAIN ?? "n/a"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="The git SHA of the commit the deployment was triggered by. Example: fa1eade47b73733d6312d5abfad33ce9e4068081.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="text-sm font-medium text-gray-700">
									VERCEL_GIT_COMMIT_SHA
								</span>
								<span className="text-sm break-all text-gray-900">
									{VERCEL_GIT_COMMIT_SHA ?? "n/a"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="The message attached to the commit the deployment was triggered by. Example: Update about page.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="text-sm font-medium text-gray-700">
									VERCEL_GIT_COMMIT_MESSAGE
								</span>
								<span className="text-sm text-gray-900">
									{VERCEL_GIT_COMMIT_MESSAGE ?? "n/a"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="The name attached to the author of the commit that the project was deployed by. Example: John Doe.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="text-sm font-medium text-gray-700">
									VERCEL_GIT_COMMIT_AUTHOR_NAME
								</span>
								<span className="text-gray-900">
									{VERCEL_GIT_COMMIT_AUTHOR_NAME ?? "n/a"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Nonce value for CSP (Content Security Policy) headers.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									SERVER_CSP_NONCE
								</span>
								<span className="text-gray-900">
									{SERVER_CSP_NONCE ?? "n/a"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Indicates whether the app is using a mock backend.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									MOCK_BACKEND
								</span>
								<span className="text-gray-900">
									{MOCK_BACKEND ?? "n/a"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="The API URL used for Storybook integration.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									STORYBOOK_API_URL
								</span>
								<span className="text-sm break-all text-gray-900">
									{STORYBOOK_API_URL ?? "n/a"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="The Vitest worker ID used for test runs.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									VITEST_WORKER_ID
								</span>
								<span className="text-gray-900">
									{VITEST_WORKER_ID ?? "n/a"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Indicates whether i18n debug mode is enabled.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									DEBUG_I18N
								</span>
								<span className="text-gray-900">
									{DEBUG_I18N ?? "n/a"}
								</span>
							</div>
						</Tooltip>
					</div>
				</CardBody>
			</CardWrapper>

			{/* Integrations Section */}
			<CardWrapper>
				<CardBody>
					<Heading
						as="h2"
						styleAs="h5"
						className="mb-4 border-b border-gray-200 pb-2"
					>
						Integrations
					</Heading>
					<div className="space-y-3">
						<Tooltip content="The Google Maps API key for embedding maps.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									GOOGLE_MAPS_API_KEY
								</span>
								<span className="text-sm break-all text-gray-900">
									{GOOGLE_MAPS_API_KEY ?? "n/a"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="The Google Maps ID for customizing map styling.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									GOOGLE_MAPS_ID
								</span>
								<span className="text-gray-900">
									{GOOGLE_MAPS_ID ?? "n/a"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="The Google Tag Manager container ID.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									GTM_CONTAINER_ID
								</span>
								<span className="text-gray-900">
									{GTM_CONTAINER_ID ?? "n/a"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Plausible Analytics site domain (must match the site registered in the Plausible dashboard). Empty disables the tracker.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									PLAUSIBLE_DOMAIN
								</span>
								<span className="text-gray-900">
									{PLAUSIBLE_DOMAIN || "n/a"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Freshdesk widget ID">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									FRESHDESK_WIDGET_ID
								</span>
								<span className="text-gray-900">
									{FRESHDESK_WIDGET_ID ?? "n/a"}
								</span>
							</div>
						</Tooltip>
					</div>
				</CardBody>
			</CardWrapper>

			{/* Links Section */}
			<CardWrapper>
				<CardBody>
					<Heading
						as="h2"
						styleAs="h5"
						className="mb-4 border-b border-gray-200 pb-2"
					>
						API & External Links
					</Heading>
					<div className="space-y-3">
						<Tooltip content="The base URL for the API services.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									API_URL
								</span>
								<span className="text-gray-900">
									{API_URL ? (
										<a
											href={API_URL}
											target="_blank"
											rel="noreferrer"
											className="break-all text-blue-600 underline hover:text-blue-800"
										>
											{API_URL}
										</a>
									) : (
										"n/a"
									)}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="The GraphQL API endpoint used by the app.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									GRAPHQL_URL
								</span>
								<span className="text-gray-900">
									{GRAPHQL_URL ? (
										<a
											href={GRAPHQL_URL}
											target="_blank"
											rel="noreferrer"
											className="break-all text-blue-600 underline hover:text-blue-800"
										>
											{GRAPHQL_URL}
										</a>
									) : (
										"n/a"
									)}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="URL for the authentication login process.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									AUTH_LOGIN_URL
								</span>
								<span className="text-gray-900">
									{AUTH_LOGIN_URL ? (
										<a
											href={AUTH_LOGIN_URL}
											target="_blank"
											rel="noreferrer"
											className="break-all text-blue-600 underline hover:text-blue-800"
										>
											{AUTH_LOGIN_URL}
										</a>
									) : (
										"n/a"
									)}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="URL for the authentication logout process.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									AUTH_LOGOUT_URL
								</span>
								<span className="text-gray-900">
									{AUTH_LOGOUT_URL ? (
										<a
											href={AUTH_LOGOUT_URL}
											target="_blank"
											rel="noreferrer"
											className="break-all text-blue-600 underline hover:text-blue-800"
										>
											{AUTH_LOGOUT_URL}
										</a>
									) : (
										"n/a"
									)}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="URL for the authentication MFA process.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									AUTH_MFA_URL
								</span>
								<span className="text-gray-900">
									{AUTH_MFA_URL ? (
										<a
											href={AUTH_MFA_URL}
											target="_blank"
											rel="noreferrer"
											className="break-all text-blue-600 underline hover:text-blue-800"
										>
											{AUTH_MFA_URL}
										</a>
									) : (
										"n/a"
									)}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Admin panel for external platform management.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="text-sm font-medium text-gray-700">
									EXTERNAL_PLATFORM_ADMIN_PANEL
								</span>
								<span className="text-gray-900">
									{EXTERNAL_PLATFORM_ADMIN_PANEL ? (
										<a
											href={EXTERNAL_PLATFORM_ADMIN_PANEL}
											target="_blank"
											rel="noreferrer"
											className="text-sm break-all text-blue-600 underline hover:text-blue-800"
										>
											{EXTERNAL_PLATFORM_ADMIN_PANEL}
										</a>
									) : (
										"n/a"
									)}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Link to the external platform's investment center.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="text-sm font-medium text-gray-700">
									EXTERNAL_PLATFORM_ZONHUB_INVESTMENT
								</span>
								<span className="text-gray-900">
									{EXTERNAL_PLATFORM_ZONHUB_INVESTMENT ? (
										<a
											href={
												EXTERNAL_PLATFORM_ZONHUB_INVESTMENT
											}
											target="_blank"
											rel="noreferrer"
											className="text-sm break-all text-blue-600 underline hover:text-blue-800"
										>
											{
												EXTERNAL_PLATFORM_ZONHUB_INVESTMENT
											}
										</a>
									) : (
										"n/a"
									)}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Link to the external platform's information center.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="text-sm font-medium text-gray-700">
									EXTERNAL_PLATFORM_INFO_CENTER
								</span>
								<span className="text-gray-900">
									{EXTERNAL_PLATFORM_INFO_CENTER ? (
										<a
											href={EXTERNAL_PLATFORM_INFO_CENTER}
											target="_blank"
											rel="noreferrer"
											className="text-sm break-all text-blue-600 underline hover:text-blue-800"
										>
											{EXTERNAL_PLATFORM_INFO_CENTER}
										</a>
									) : (
										"n/a"
									)}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Link to the external platform's company statistics.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="text-sm font-medium text-gray-700">
									EXTERNAL_PLATFORM_ZONHUB_STATS_COMPANY
								</span>
								<span className="text-gray-900">
									{EXTERNAL_PLATFORM_ZONHUB_STATS_COMPANY ? (
										<a
											href={
												EXTERNAL_PLATFORM_ZONHUB_STATS_COMPANY
											}
											target="_blank"
											rel="noreferrer"
											className="text-sm break-all text-blue-600 underline hover:text-blue-800"
										>
											{
												EXTERNAL_PLATFORM_ZONHUB_STATS_COMPANY
											}
										</a>
									) : (
										"n/a"
									)}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Link to the external platform's finance center.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="text-sm font-medium text-gray-700">
									EXTERNAL_PLATFORM_ZONHUB_FINANCE
								</span>
								<span className="text-gray-900">
									{EXTERNAL_PLATFORM_ZONHUB_FINANCE ? (
										<a
											href={
												EXTERNAL_PLATFORM_ZONHUB_FINANCE
											}
											target="_blank"
											rel="noreferrer"
											className="text-sm break-all text-blue-600 underline hover:text-blue-800"
										>
											{EXTERNAL_PLATFORM_ZONHUB_FINANCE}
										</a>
									) : (
										"n/a"
									)}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Google review URL opened from the rating modal when a user gives 4-5 stars.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="text-sm font-medium text-gray-700">
									EXTERNAL_GOOGLE_REVIEW_URL
								</span>
								<span className="text-gray-900">
									{EXTERNAL_GOOGLE_REVIEW_URL ? (
										<a
											href={EXTERNAL_GOOGLE_REVIEW_URL}
											target="_blank"
											rel="noreferrer"
											className="text-sm break-all text-blue-600 underline hover:text-blue-800"
										>
											{EXTERNAL_GOOGLE_REVIEW_URL}
										</a>
									) : (
										"n/a"
									)}
								</span>
							</div>
						</Tooltip>
					</div>
				</CardBody>
			</CardWrapper>

			{/* Error Logging Section */}
			<CardWrapper>
				<CardBody>
					<Heading
						as="h2"
						styleAs="h5"
						className="mb-4 border-b border-gray-200 pb-2"
					>
						Error Logging & Monitoring
					</Heading>
					<div className="space-y-3">
						<Tooltip content="Is Sentry enabled?">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									SENTRY_ENABLED
								</span>
								<span
									className={`font-medium ${SENTRY_ENABLED === "true" ? "text-green-600" : "text-gray-900"}`}
								>
									{SENTRY_ENABLED === "true"
										? "Enabled"
										: "Disabled"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="Indicates whether Sentry debug mode is enabled.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									SENTRY_DEBUG
								</span>
								<span
									className={`font-medium ${SENTRY_DEBUG === "true" ? "text-yellow-600" : "text-gray-900"}`}
								>
									{SENTRY_DEBUG === "true"
										? "Debug Mode On"
										: "Debug Mode Off"}
								</span>
							</div>
						</Tooltip>

						<Tooltip content="The Sentry DSN for error monitoring.">
							<div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100">
								<span className="font-medium text-gray-700">
									SENTRY_DSN
								</span>
								<span className="text-sm break-all text-gray-900">
									{SENTRY_DSN ?? "n/a"}
								</span>
							</div>
						</Tooltip>

						<div className="pt-3">
							<Button
								onClick={() => {
									throw new Error("Test throw error")
								}}
							>
								Test Sentry Error
							</Button>
						</div>
					</div>
				</CardBody>
			</CardWrapper>

			{/* Service Worker Testing Section */}
			<CardWrapper>
				<CardBody>
					<Heading
						as="h2"
						styleAs="h5"
						className="mb-4 border-b border-gray-200 pb-2"
					>
						Service Worker Testing
					</Heading>
					<ServiceWorkerTester />
				</CardBody>
			</CardWrapper>

			{/* PWA Info Section */}
			<CardWrapper>
				<CardBody>
					<Heading
						as="h2"
						styleAs="h5"
						className="mb-4 border-b border-gray-200 pb-2"
					>
						PWA Information
					</Heading>
					<div className="rounded-lg bg-gray-50 p-4">
						<div className="max-h-[400px] overflow-y-auto">
							<pre className="text-xs text-gray-700">
								{JSON.stringify(PWAInfo, null, 2)}
							</pre>
						</div>
					</div>
				</CardBody>
			</CardWrapper>
		</div>
	)
}

/**
 * ServiceWorkerTester Component
 * Provides testing functionality for service worker updates
 */
function ServiceWorkerTester() {
	const [status, setStatus] = useState<string>("")
	const [statusType, setStatusType] = useState<"success" | "error" | "info">(
		"info",
	)
	const [logs, setLogs] = useState<string[]>([])
	const [swRegistration, setSwRegistration] =
		useState<ServiceWorkerRegistration | null>(null)

	const log = (message: string) => {
		const timestamp = new Date().toLocaleTimeString()
		const logMessage = `[${timestamp}] ${message}`
		setLogs((prev) => [...prev, logMessage])
		console.log(logMessage)
	}

	const updateStatus = (
		message: string,
		type: "success" | "error" | "info" = "info",
	) => {
		setStatus(message)
		setStatusType(type)
	}

	useEffect(() => {
		// Check if service worker is supported
		if ("serviceWorker" in navigator) {
			log("Service Worker is supported")

			// Register service worker
			navigator.serviceWorker
				.register("/sw.js", { scope: "/" })
				.then((registration) => {
					log("Service Worker registered successfully")
					updateStatus("Service Worker registered successfully")
					setSwRegistration(registration)

					// Check for updates
					registration.addEventListener("updatefound", () => {
						log("Service Worker update found!")
						updateStatus("Service Worker update found!", "info")
					})

					// Listen for service worker state changes
					registration.addEventListener("statechange", () => {
						log(
							`Service Worker state changed to: ${registration.active?.state ?? "unknown"}`,
						)
					})
					return
				})
				.catch((error) => {
					log(`Service Worker registration failed: ${error}`)
					updateStatus(`Service Worker registration failed: ${error}`)
				})
		} else {
			log("Service Worker is not supported")
			updateStatus("Service Worker is not supported", "error")
		}

		// Listen for messages from service worker
		navigator.serviceWorker.addEventListener("message", (event) => {
			log(`Message from Service Worker: ${JSON.stringify(event.data)}`)
		})
	}, [])

	const checkForUpdates = () => {
		if (swRegistration) {
			log("Checking for service worker updates...")
			swRegistration
				.update()
				.then(() => {
					log("Update check completed")
					updateStatus("Update check completed", "success")
					return
				})
				.catch((error) => {
					log(`Update check failed: ${error}`)
					updateStatus(`Update check failed: ${error}`, "error")
				})
		} else {
			log("No service worker registration found")
			updateStatus("No service worker registration found", "error")
		}
	}

	const reloadPage = () => {
		log("Reloading page...")
		window.location.reload()
	}

	const clearCache = async () => {
		if ("caches" in window) {
			log("Clearing all caches...")
			try {
				const cacheNames = await caches.keys()
				await Promise.all(
					cacheNames.map(async (cacheName) => {
						log(`Deleting cache: ${cacheName}`)
						return caches.delete(cacheName)
					}),
				)
				log("All caches cleared")
				updateStatus("All caches cleared", "success")
			} catch (error) {
				log(`Cache clearing failed: ${error}`)
				updateStatus(`Cache clearing failed: ${error}`, "error")
			}
		} else {
			log("Cache API not supported")
			updateStatus("Cache API not supported", "error")
		}
	}

	const clearLogs = () => {
		setLogs([])
	}

	return (
		<div className="space-y-4">
			{/* Status Display */}
			{status && (
				<div
					className={`rounded-md p-3 ${
						statusType === "success"
							? "border border-green-200 bg-green-100 text-green-800"
							: statusType === "error"
								? "border border-red-200 bg-red-100 text-red-800"
								: "border border-blue-200 bg-blue-100 text-blue-800"
					}`}
				>
					{status}
				</div>
			)}

			{/* Action Buttons */}
			<div className="flex flex-wrap gap-2">
				<Button
					onClick={checkForUpdates}
					className="bg-blue-600 hover:bg-blue-700"
				>
					Check for Updates
				</Button>
				<Button
					onClick={reloadPage}
					className="bg-gray-600 hover:bg-gray-700"
				>
					Reload Page
				</Button>
				<Button
					onClick={clearCache}
					className="bg-orange-600 hover:bg-orange-700"
				>
					Clear Cache
				</Button>
				<Button onClick={clearLogs}>Clear Logs</Button>
			</div>

			{/* Service Worker Info */}
			<div className="grid grid-cols-2 gap-4 text-sm">
				<div>
					<h3 className="font-semibold">Service Worker Status</h3>
					<p>
						Supported: {"serviceWorker" in navigator ? "Yes" : "No"}
					</p>
					<p>Registered: {swRegistration ? "Yes" : "No"}</p>
					<p>State: {swRegistration?.active?.state ?? "Unknown"}</p>
				</div>
				<div>
					<h3 className="font-semibold">Cache Status</h3>
					<p>
						Cache API:{" "}
						{"caches" in window ? "Supported" : "Not Supported"}
					</p>
					<p>Logs: {logs.length} entries</p>
				</div>
			</div>

			{/* Logs Display */}
			<div className="max-h-64 overflow-y-auto rounded-md bg-gray-100 p-3">
				<h3 className="mb-2 font-semibold">Logs</h3>
				{logs.length === 0 ? (
					<p className="text-sm text-gray-500">
						No logs yet. Try checking for updates or clearing cache.
					</p>
				) : (
					<div className="space-y-1 text-xs">
						{logs.map((log, index) => (
							<div key={index} className="font-mono">
								{log}
							</div>
						))}
					</div>
				)}
			</div>
		</div>
	)
}
